// src/components/review/ErelEntityReviewList.js
// Tab "Empresas EREL" do Review Workbench — G3B_SPEC v1.0 (Loop 1).
// Lista ai_proposals(proposal_type='entity_create', review_status='pending') e
// registra decisões EXCLUSIVAMENTE via RPC fn_erel_review_proposal (6 botões).
//
// Decisões de design (G3B_SPEC §§2-6):
//   - contador "decididas X/Y" no topo (gate de saída = 20 propostas, §9)
//   - domínio de origem vem de erel_jobs.raw_mention (join por source_id)
//   - reasons do picker vêm de tipos_review_reason (cache por botão)
//   - erros do RPC são contrato de UI (§6) → exibidos LITERALMENTE em toast
//   - sucesso → toast com review_status + apply_result.mode (quando houver)
//   - sem update otimista: decisão é transacional no banco; re-load após sucesso

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { supabase } from '../../supabaseClient';
import ErelEntityCard from './ErelEntityCard';

const SUCCESS_TOAST_MS = 3500;
const ERROR_TOAST_MS = 7000; // mensagens contratuais §6 são longas — tempo para ler

export default function ErelEntityReviewList({ onChange }) {
  const [proposals, setProposals] = useState([]);
  const [decidedCount, setDecidedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [toast, setToast] = useState(null);
  const reasonsCacheRef = useRef({}); // button -> rows de tipos_review_reason
  const toastTimerRef = useRef(null);

  const showToast = useCallback((message, tone = 'success') => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ message, tone });
    toastTimerRef.current = setTimeout(
      () => setToast(null),
      tone === 'error' ? ERROR_TOAST_MS : SUCCESS_TOAST_MS,
    );
  }, []);

  useEffect(() => () => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    const [pend, dec] = await Promise.all([
      supabase
        .from('ai_proposals')
        .select('id, source_id, agent_name, ai_confidence, proposed_data, created_at')
        .eq('proposal_type', 'entity_create')
        .eq('review_status', 'pending')
        .order('created_at', { ascending: true }),
      supabase
        .from('ai_proposals')
        .select('*', { count: 'exact', head: true })
        .eq('proposal_type', 'entity_create')
        .neq('review_status', 'pending'),
    ]);

    if (pend.error) {
      setError(pend.error.message);
      setProposals([]);
      setLoading(false);
      return;
    }

    let rows = pend.data || [];

    // domínio de origem do card = erel_jobs.raw_mention (G3B_SPEC §2.4 source_mention)
    const sourceIds = [...new Set(rows.map((r) => r.source_id).filter(Boolean))];
    if (sourceIds.length > 0) {
      const { data: jobs, error: jobsErr } = await supabase
        .from('erel_jobs')
        .select('id, raw_mention')
        .in('id', sourceIds);
      if (!jobsErr) {
        const byId = new Map((jobs || []).map((j) => [j.id, j.raw_mention]));
        rows = rows.map((r) => ({ ...r, raw_mention: byId.get(r.source_id) || null }));
      }
    }

    setProposals(rows);
    setDecidedCount(dec.count ?? 0);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Picker de reasons (G3B_SPEC §2.3) — cache por botão, vocabulário é estável na sessão
  const fetchReasons = useCallback(async (button) => {
    if (reasonsCacheRef.current[button]) return reasonsCacheRef.current[button];
    const { data, error: err } = await supabase
      .from('tipos_review_reason')
      .select('value, label_pt, requires_note')
      .eq('button', button)
      .eq('is_active', true)
      .order('display_order');
    if (err) throw err;
    reasonsCacheRef.current[button] = data || [];
    return reasonsCacheRef.current[button];
  }, []);

  const decide = useCallback(
    async ({ proposalId, decision, reasonCode, humanNote, decidedInSeconds }) => {
      setBusyId(proposalId);
      const { data, error: err } = await supabase.rpc('fn_erel_review_proposal', {
        p_proposal_id: proposalId,
        p_decision: decision,
        p_reason_code: reasonCode,
        p_human_note: humanNote || null,
        p_decided_in_seconds: decidedInSeconds ?? null,
      });
      setBusyId(null);

      if (err) {
        // contrato §6: mensagem do RPC exibida literalmente
        showToast(err.message, 'error');
        return false;
      }

      const applyMode = data?.apply_result?.mode;
      showToast(
        `Decisão registrada: ${data?.review_status || decision}${
          applyMode ? ` · apply: ${applyMode}` : ''
        }`,
        'success',
      );
      await load();
      if (typeof onChange === 'function') onChange();
      return true;
    },
    [load, onChange, showToast],
  );

  const total = decidedCount + proposals.length;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs text-slate-600 font-medium">
          {loading ? (
            'Carregando…'
          ) : (
            <>
              decididas{' '}
              <span className="text-sm font-bold text-slate-900">
                {decidedCount}/{total}
              </span>
              {proposals.length > 0 && (
                <span className="ml-2 text-slate-400">
                  · {proposals.length} pendente{proposals.length === 1 ? '' : 's'}
                </span>
              )}
            </>
          )}
        </div>
        {!loading && total > 0 && (
          <div className="flex-1 max-w-[200px] h-1.5 bg-slate-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-emerald-500 transition-all"
              style={{ width: `${Math.round((decidedCount / total) * 100)}%` }}
            />
          </div>
        )}
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded text-xs text-red-800 flex items-start gap-2">
          <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {!loading && !error && proposals.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-12 text-center">
          <p className="text-sm text-slate-600">Nenhuma proposta entity_create pendente. 🎉</p>
          <p className="text-xs text-slate-400 mt-1">
            Gate G3B concluído — o D2G consome os learning_signals a partir daqui.
          </p>
        </div>
      )}

      {proposals.map((p) => (
        <ErelEntityCard
          key={p.id}
          proposal={p}
          busy={busyId === p.id}
          fetchReasons={fetchReasons}
          onDecide={decide}
        />
      ))}

      {toast && (
        <div
          className={`fixed bottom-4 right-4 z-50 max-w-md px-3 py-2 rounded shadow-lg text-xs font-medium ${
            toast.tone === 'error' ? 'bg-red-600 text-white' : 'bg-emerald-600 text-white'
          }`}
          role="status"
        >
          {toast.message}
        </div>
      )}
    </div>
  );
}
